import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { trackClick } from "@/shared/utils/analytics";

type SubItem = {
  label: string;
  path?: string;
  sectionId?: string;
  external?: string;
  description?: string;
};

type NavItem = {
  label: string;
  path?: string;
  sectionId?: string;
  external?: string;
  subItems?: SubItem[];
};

interface DesktopNavProps {
  navItems: NavItem[];
  onNavigate: (item: NavItem | SubItem) => void;
}

export const DesktopNav = ({ navItems, onNavigate }: DesktopNavProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideDropdown = Object.values(dropdownRefs.current).some(
        ref => ref && ref.contains(target)
      );
      if (!isInsideDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150); // Small delay to allow moving to dropdown
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleItemClick = (item: NavItem | SubItem) => {
    trackClick(item.label, "navbar");
    setOpenDropdown(null);
    onNavigate(item);
  };

  return (
    <div className="hidden md:flex items-center justify-center flex-1 mx-12">
      <nav className="flex items-center gap-6">
        {navItems.map((item, index) => (
          <div
            key={item.path || item.sectionId || index}
            className="relative"
            ref={ref => dropdownRefs.current[item.label] = ref}
            onMouseEnter={() => item.subItems && handleMouseEnter(item.label)}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              className="flex items-center gap-1 text-white hover:text-[#6dd8d0] transition-colors font-normal py-2 tracking-[-0.011em]"
              onClick={() => !item.subItems && handleItemClick(item)}
              style={{ fontFamily: "var(--font-sans)", fontSize: "18px" }}
            >
              {item.label}
              {item.subItems && (
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    openDropdown === item.label ? "rotate-180" : ""
                  }`}
                />
              )}
            </button>

            {/* Dropdown Menu */}
            {item.subItems && openDropdown === item.label && (
              <div
                className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-white/10 bg-[#0a1f24] shadow-2xl overflow-hidden"
                onMouseEnter={handleDropdownMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <div className="py-2">
                  {item.subItems.map((subItem, subIndex) => (
                    <button
                      key={subItem.path || subItem.sectionId || subIndex}
                      className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors group"
                      onClick={() => handleItemClick(subItem)}
                    >
                      <div className="space-y-1">
                        <div className="text-white group-hover:text-[#6dd8d0] transition-colors text-sm font-medium">
                          {subItem.label}
                        </div>
                        {subItem.description && (
                          <div className="text-white/60 text-xs leading-relaxed">
                            {subItem.description}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
};